import React, { useCallback, useContext, useEffect, useState } from 'react';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/IntentsPage';
import block from 'propmods';

import { ApiError, granetApi } from '../../api';
import { useSettingsPageContext } from '../../context/settingsPageContext';
import { Context as SnapshotContext } from '../../context/snapshotContext';
import { useCurrentAliceApp } from '../../hooks/useCurrentAliceApp';
import { useFetchingHandler } from '../../hooks/useFetchingHandler';
import { CrossIcon } from '../../icons/cross';
import { granetEntitiesCompletionProvider } from '../../lib/monaco/languages/granet/completion';
import { GrammarValidationErrorPayload } from '../../model/grammar';
import { GrammarValidationAction, grammarValidationReducer } from '../../reducers/tooltips/grammarValidationReducer';
import { ActionButton } from '../ActionButton/ActionButton';
import { GranetCodeEditor } from '../GranetCodeEditor/GranetCodeEditor';

import './GrammarPageEntitiesSettings.scss';

const t = i18n(keyset);
const b = block('GrammarPageEntitiesSettings');

interface Props {
    onClose: () => void;
    afterSave: () => void;
    setIsChanged: (isChanged: boolean) => void;
}

export const GrammarPageEntitiesSettings: React.FC<Props> = ({ onClose, setIsChanged, afterSave }) => {
    const skill = useCurrentAliceApp()!;
    const { refreshSnapshot } = useContext(SnapshotContext);
    const { isDraft, isEditable } = useSettingsPageContext();

    const [validationError, setValidationError] = useState<GrammarValidationErrorPayload | undefined>(undefined);

    const initialEntities = isDraft ? skill.draft.customEntities : skill.customEntities;
    const [entitiesCode, setEntitiesCode] = useState(initialEntities ?? '');

    const onEntitiesChange = useCallback(
        (value: string) => {
            setEntitiesCode(value);

            setIsChanged(true);
        },
        [setIsChanged],
    );

    const [onSave, savingState] = useFetchingHandler({
        handler: granetApi.updateCustomEntities,
        throwErrors: true,
        afterLoad: refreshSnapshot,
    });

    useEffect(() => {
        setValidationError(undefined);
    }, [entitiesCode, savingState]);

    const onSaveAction = async (): Promise<GrammarValidationAction> => {
        try {
            await onSave({ sourceText: entitiesCode, skillId: skill.id }, skill.channel);

            afterSave();

            return 'ok';
        } catch (e) {
            if (e instanceof ApiError && e.isGrammarValidationError()) {
                setValidationError(e.payload);

                return 'validation-error';
            }

            if (e instanceof ApiError && e.isCustomEntityInUseError()) {
                return 'in-use';
            }

            if (e instanceof ApiError && e.payload?.errorCode === 'GRAMMAR_SIZE_LIMIT') {
                return 'size-exceed';
            }

            return 'error';
        }
    };

    const isSaving = savingState === 'loading';

    return (
        <div {...b()}>
            <div {...b('close')} onClick={onClose}>
                <CrossIcon color="#979797" />
            </div>
            <h2 {...b('heading')}>{t('entities-title')}</h2>
            <div {...b('editor')}>
                <GranetCodeEditor
                    onChange={(code) => onEntitiesChange(code)}
                    code={entitiesCode}
                    validationError={validationError}
                    onSave={onSaveAction}
                    readOnly={!isEditable || isSaving}
                    completionProvider={granetEntitiesCompletionProvider}
                />
            </div>
            {isEditable && (
                <div {...b('buttons')}>
                    <ActionButton
                        tooltipReducer={grammarValidationReducer}
                        action={onSaveAction}
                        disabled={isSaving}
                        defaultTooltipState={{ tooltipVisible: false }}
                        theme="action"
                    >
                        {t('button-save')}
                    </ActionButton>
                </div>
            )}
        </div>
    );
};
